import React, { useState, useEffect } from 'react';

// Icons
const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const TrendingDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
    <polyline points="17 18 23 18 23 12"></polyline>
  </svg>
);

const BarChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10"></line>
    <line x1="18" y1="20" x2="18" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="16"></line>
  </svg>
);

const LineChartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18"></path>
    <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"></path>
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const metrics = [
    {
      label: 'Total de Vendas',
      value: 'R$ 125.430',
      change: '+12.5%',
      trend: 'up',
      color: 'var(--color-success-500)',
      bgColor: 'rgba(34, 197, 94, 0.1)'
    },
    {
      label: 'Vendas Ganhas',
      value: '47',
      change: '+8.3%',
      trend: 'up',
      color: 'var(--color-success-500)',
      bgColor: 'rgba(34, 197, 94, 0.1)'
    },
    {
      label: 'Taxa de Conversão',
      value: '23.5%',
      change: '-2.1%',
      trend: 'down',
      color: 'var(--color-error-500)',
      bgColor: 'rgba(239, 68, 68, 0.1)'
    },
    {
      label: 'Ticket Médio',
      value: 'R$ 2.668',
      change: '+4.2%',
      trend: 'up',
      color: 'var(--color-success-500)',
      bgColor: 'rgba(34, 197, 94, 0.1)'
    }
  ];

  const performers = [
    { name: 'João Silva', sales: 'R$ 45.230', deals: 18, conversion: '28%' },
    { name: 'Maria Santos', sales: 'R$ 38.450', deals: 15, conversion: '26%' },
    { name: 'Pedro Costa', sales: 'R$ 32.100', deals: 12, conversion: '24%' },
    { name: 'Ana Oliveira', sales: 'R$ 28.650', deals: 11, conversion: '23%' },
    { name: 'Carlos Lima', sales: 'R$ 25.980', deals: 10, conversion: '22%' },
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 'var(--space-16)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: '3px solid var(--color-primary-200)',
          borderTopColor: 'var(--color-primary-600)',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{
          fontSize: 'var(--text-3xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-2)'
        }}>
          Analytics
        </h1>
        <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-secondary)' }}>
          Insights avançados sobre seu desempenho
        </p>
      </div>

      {/* Period Selector */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-4)',
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--glass-border)',
        marginBottom: 'var(--space-6)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <CalendarIcon />
          <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>Período:</span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {(['week', 'month', 'quarter', 'year'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                border: 'none',
                background: period === p ? 'var(--gradient-primary)' : 'var(--glass-bg)',
                color: period === p ? 'white' : 'var(--text-secondary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)'
              }}
            >
              {p === 'week' && 'Semana'}
              {p === 'month' && 'Mês'}
              {p === 'quarter' && 'Trimestre'}
              {p === 'year' && 'Ano'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-6)'
      }}>
        {metrics.map((metric, index) => (
          <div
            key={index}
            style={{
              padding: 'var(--space-5)',
              background: 'var(--glass-bg-strong)',
              backdropFilter: 'var(--glass-blur)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--glass-border)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
              <div>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                  {metric.label}
                </p>
                <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--text-primary)' }}>
                  {metric.value}
                </p>
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-lg)',
                background: metric.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: metric.color
              }}>
                {metric.trend === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <span style={{ color: metric.color, fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>
                {metric.change}
              </span>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                vs período anterior
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: 'var(--space-6)',
        marginBottom: 'var(--space-6)'
      }}>
        {/* Sales Trend Chart */}
        <div style={{
          padding: 'var(--space-5)',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
            <div>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                Tendência de Vendas
              </h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                Evolução ao longo do tempo
              </p>
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-lg)',
              background: 'rgba(59, 130, 246, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-info-500)'
            }}>
              <LineChartIcon />
            </div>
          </div>
          <div style={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
              <LineChartIcon />
              <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
                Gráfico será implementado na próxima etapa
              </p>
            </div>
          </div>
        </div>

        {/* Deals by Stage Chart */}
        <div style={{
          padding: 'var(--space-5)',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
            <div>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
                Vendas por Etapa
              </h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                Distribuição no pipeline
              </p>
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--radius-lg)',
              background: 'rgba(168, 85, 247, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#a855f7'
            }}>
              <BarChartIcon />
            </div>
          </div>
          <div style={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>
              <BarChartIcon />
              <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
                Gráfico será implementado na próxima etapa
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div style={{
        padding: 'var(--space-5)',
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--glass-border)',
        marginBottom: 'var(--space-6)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-4)' }}>
          <div>
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
              Melhores Performers
            </h3>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              Membros da equipe com melhor desempenho
            </p>
          </div>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(168, 85, 247, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#a855f7'
          }}>
            <TrendingUpIcon />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {performers.map((performer, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-4)',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                transition: 'all var(--transition-fast)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--gradient-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--text-sm)'
                }}>
                  {index + 1}
                </div>
                <div>
                  <p style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                    {performer.name}
                  </p>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                    {performer.deals} vendas • {performer.conversion} conversão
                  </p>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
                  {performer.sales}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Info Banner */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        background: 'rgba(99, 102, 241, 0.1)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid rgba(99, 102, 241, 0.3)'
      }}>
        <div style={{ color: 'var(--color-primary-600)', marginTop: '2px' }}>
          <InfoIcon />
        </div>
        <div>
          <h4 style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
            Funcionalidades Em Desenvolvimento
          </h4>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
            Esta é a página base de Analytics. Na próxima etapa serão implementados:
            Gráficos interativos, Análise de tendências, Comparação de períodos,
            Dashboard personalizado, Filtros avançados e Exportação de insights.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Analytics;
